import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface Series {
  id: number;
  name: string;
  type?: string;
  description?: string;
  imageURL?: string;
  subjects?: any[];
}

const SeriesPage = () => {
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSeries, setEditingSeries] = useState<Series | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    imageURL: '',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter series based on search term
  const filteredSeries = useMemo(() => {
    if (!searchTerm) return series;
    
    return series.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.type && item.type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [series, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.getSeries();
      setSeries(data);
    } catch (error) {
      console.error('Error loading series:', error);
      toast({
        title: 'Error',
        description: 'Failed to load series',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (editingSeries) {
        await api.updateSeries(editingSeries.id, formData);
        toast({
          title: 'Success',
          description: 'Series updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.createSeries(formData);
        toast({
          title: 'Success',
          description: 'Series created successfully',
          status: 'success',
          duration: 3000,
        });
      }
      
      resetForm();
      onClose();
      loadData();
    } catch (error) {
      console.error('Error saving series:', error);
      toast({
        title: 'Error',
        description: 'Failed to save series',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this series?')) {
      try {
        await api.deleteSeries(id);
        toast({
          title: 'Success',
          description: 'Series deleted successfully',
          status: 'success',
          duration: 3000,
        });
        loadData();
      } catch (error) {
        console.error('Error deleting series:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete series',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleEdit = (seriesItem: Series) => {
    setEditingSeries(seriesItem);
    setFormData({
      name: seriesItem.name || '',
      type: seriesItem.type || '',
      description: seriesItem.description || '',
      imageURL: seriesItem.imageURL || '',
    });
    onOpen();
  };

  const resetForm = () => {
    setFormData({ name: '', type: '', description: '', imageURL: '' });
    setEditingSeries(null);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          📚 Series Management
        </Heading>
        <Button colorScheme="blue" onClick={() => { resetForm(); onOpen(); }}>
          ➕ Add Series
        </Button>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search series by name, type, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredSeries.length} of {series.length} series
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Series Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <Text>Loading...</Text>
          ) : filteredSeries.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No series found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No series found. Create your first series!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Image</Th>
                  <Th>Description</Th>
                  <Th>Subjects Count</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSeries.map((seriesItem) => (
                  <Tr key={seriesItem.id}>
                    <Td fontWeight="medium">{seriesItem.name}</Td>
                    <Td>
                      {seriesItem.type ? (
                        <Badge 
                          colorScheme={seriesItem.type.toLowerCase().includes('hindi') ? 'orange' : 'green'}
                        >
                          {seriesItem.type}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">Not set</Text>
                      )}
                    </Td>
                    <Td>
                      {seriesItem.imageURL ? (
                        <img 
                          src={seriesItem.imageURL} 
                          alt={seriesItem.name}
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                          }}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500">No image</Text>
                      )}
                    </Td>
                    <Td maxW="200px" isTruncated>
                      {seriesItem.description || 'No description'}
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {seriesItem.subjects?.length || 0} subjects
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<Text>✏️</Text>}
                          aria-label="Edit"
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(seriesItem)}
                        />
                        <IconButton
                          icon={<Text>🗑️</Text>}
                          aria-label="Delete"
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(seriesItem.id)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingSeries ? 'Edit Series' : 'Create New Series'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Series Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., mathematics-series"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Medium Type</FormLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    placeholder="Select medium"
                  >
                    <option value="Hindi Medium">Hindi Medium</option>
                    <option value="English Medium">English Medium</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this series..."
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Image URL</FormLabel>
                  <Input
                    value={formData.imageURL}
                    onChange={(e) => setFormData({ ...formData, imageURL: e.target.value })}
                    placeholder="Enter image URL"
                  />
                  {formData.imageURL && (
                    <Box mt={2}>
                      <img 
                        src={formData.imageURL} 
                        alt="Series preview" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '200px', 
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }} 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://via.placeholder.com/200x200?text=Invalid+Image+URL';
                        }}
                      />
                    </Box>
                  )}
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue" isLoading={loading}>
                {editingSeries ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default SeriesPage; 